
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, X, FileImage, FileText } from "lucide-react";

interface RevenueDownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (format: 'jpg' | 'pdf', startPage?: number, endPage?: number) => void;
  title: string;
  description: string;
  totalItems: number;
  itemsPerPage: number;
  showPagination?: boolean;
}

const RevenueDownloadDialog = ({ 
  isOpen, 
  onClose, 
  onDownload,
  title,
  description,
  totalItems,
  itemsPerPage,
  showPagination = false
}: RevenueDownloadDialogProps) => {
  const [step, setStep] = useState<'pagination' | 'format'>('pagination');
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState<'jpg' | 'pdf'>('jpg');
  
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleNext = () => {
    if (showPagination && step === 'pagination') {
      setStep('format');
    } else {
      handleDownload();
    }
  };

  const handleDownload = () => {
    const validStartPage = Math.max(1, Math.min(startPage, totalPages));
    const validEndPage = Math.max(validStartPage, Math.min(endPage, totalPages));
    
    if (showPagination) {
      onDownload(selectedFormat, validStartPage, validEndPage);
    } else {
      onDownload(selectedFormat);
    }
    onClose();
    resetDialog();
  };

  const resetDialog = () => {
    setStep('pagination');
    setStartPage(1);
    setEndPage(1);
    setSelectedFormat('jpg');
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      resetDialog();
    }
  };

  const currentStep = showPagination ? step : 'format';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            {title}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {currentStep === 'pagination' && showPagination && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startPage">Start Page</Label>
                  <Input
                    id="startPage"
                    type="number"
                    min="1"
                    max={totalPages}
                    value={startPage}
                    onChange={(e) => setStartPage(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div>
                  <Label htmlFor="endPage">End Page</Label>
                  <Input
                    id="endPage"
                    type="number"
                    min="1"
                    max={totalPages}
                    value={endPage}
                    onChange={(e) => setEndPage(parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                Total Pages Available: {totalPages} | Records per page: {itemsPerPage}
              </div>
            </>
          )}

          {currentStep === 'format' && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Select download format:
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={selectedFormat === 'jpg' ? 'default' : 'outline'}
                  onClick={() => setSelectedFormat('jpg')}
                  className="h-20 flex flex-col gap-2"
                >
                  <FileImage className="h-6 w-6" />
                  <span>JPG Image</span>
                </Button>
                <Button
                  variant={selectedFormat === 'pdf' ? 'default' : 'outline'}
                  onClick={() => setSelectedFormat('pdf')}
                  className="h-20 flex flex-col gap-2"
                >
                  <FileText className="h-6 w-6" />
                  <span>PDF Document</span>
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-between space-x-2 pt-4 border-t">
            <div>
              {showPagination && step === 'format' && (
                <Button variant="outline" onClick={() => setStep('pagination')}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleNext} disabled={totalItems === 0}>
                <Download className="w-4 h-4 mr-2" />
                {currentStep === 'pagination' ? 'Next' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RevenueDownloadDialog;
